import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import client from '../api/client';
import { useToast } from '../hooks/use-toast';

/**
 * Small "AI assist" button that hits POST /api/ai/assist and hands the result
 * to `onResult`. Keep it lightweight so it drops into any editor header.
 *
 * Props:
 *  - feature: 'faq' | 'story' | 'travel' | 'tagline' | 'schedule'
 *  - context: extra fields for the LLM prompt (feature-specific)
 *  - onResult(data): receives the parsed JSON payload
 *  - label: button text (default "Suggest with AI")
 *  - testId
 */
export default function AiAssistButton({ feature, context = {}, onResult, label = 'Suggest with AI', testId, small = false }) {
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await client.post('/ai/assist', { feature, context });
      onResult?.(data);
      toast({ title: 'Suggestions ready ✨', description: 'Edit anything you want to keep — the rest can be tossed.' });
    } catch (e) {
      toast({ title: 'AI suggestion failed', description: e?.response?.data?.detail || e.message });
    }
    setBusy(false);
  };

  const cls = small
    ? 'text-[11px] px-2.5 py-1 rounded-full inline-flex items-center gap-1 bg-gradient-to-br from-[#F4E4DA] to-[#EFE3D8] text-[#7B2C3B] hover:from-[#EFE3D8] hover:to-[#E8D3C2] border border-[#EBD3C2] disabled:opacity-50 disabled:cursor-not-allowed'
    : 'text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 bg-gradient-to-br from-[#F4E4DA] to-[#EFE3D8] text-[#7B2C3B] hover:from-[#EFE3D8] hover:to-[#E8D3C2] border border-[#EBD3C2] disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <button data-testid={testId || `ai-assist-${feature}`} onClick={run} disabled={busy} className={cls}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
      {busy ? 'Thinking\u2026' : label}
    </button>
  );
}
